/**
 * 69.3b, the ship's crash — its look (DECISIONS 223; lane F's core/shipCrash.ts: a lost flight falls along `crashPose`,
 * comes down in 1–3 pieces, each an impact and a `shipWreck` structure gone after SHIP_CRASH.decayTicks). The owner: *"procedural
 * ship crash with terrain impact … I do think that ship should mostly crash in tact and maybe split / break on contact if it's
 * a loss of control / nacelle issue, could break up in flight as well but I think that would require extra damage in flight"*.
 *
 * - **The fall** (`FallingShip`): the flight's own model kept and placed along the crash's pose (its lean and spin are the
 *   pose's), the nacelles torn off by the loss hidden, fire streaming from where they were (the cab after a cockpit loss, the
 *   body after the hull's), a black smoke trail left in the air behind it, the remaining nacelles hunting for lift, the fans
 *   winding down. At the loss a small burst at each struck point.
 * - **The break-up** in the air (the crash's `brokeUp`): a burst, the metal thrown, and the ship drawn as its pieces from then
 *   on — the nose, the mid and the tail (or the nose and the aft; or the hull with its nacelles gone), each burning, drifting
 *   apart along the fall to where it will land (core/shipCrash.ts wreckPieces, the same table).
 * - **The impact** (`crashImpact`, at `shipCrashed`): each piece a heavy shell's burst (67.54's class), the first a fuel fire
 *   besides (a vehicle's death), and a spray of dust and metal thrown ahead along the slide.
 * - **The wreck** (`buildShipWreck`, `WreckView`): the hull on its belly, scorched (63.4's damaged pass), its nacelles torn
 *   off at the stubs, one lying by it; a piece cut across at the breaks with its torn edges. It burns a while
 *   (`SHIP_CRASH_LOOK.burn`), then smokes until it goes, sinking a little in its last seconds. Its hit shape is measured from
 *   this model (rendering/hitShapeFit.ts; constants/hitboxes.ts HITBOXES.shipWreck, SHIP_WRECK_PIECES).
 *
 * All the fire, smoke, dust and metal are pieces in 67.54's `ExplosionField` (its six instanced meshes, `emit`): the crash adds
 * no draw of its own beyond the ship it keeps and the wrecks (one baked mesh a material each). Presentation only: reads the
 * crash's pose and the wreck's age, writes nothing.
 */
import { buildHelicopter } from './helicopterLooks';
import * as THREE from 'three';
import type { GroundMaterial, ShipCrash, Vec3, WreckPiece } from '../types';
import type { UnitPalette } from './unitPalette';
import { DROPSHIP, BODY, CAB, TAIL, SPONSON, DROPSHIP_COLOURS, nacelleBody, type DropshipModel } from './dropshipLooks';
import { wreckPieces } from '../core/shipCrash';
import { SHIP_CRASH } from '../constants/action';
import { PHYS_DT } from '../constants/physics';
import { slab, top, rect, type Pts } from './vehicleLooks';
import { box, group, anchor } from './vehicleModels';
import { bakeStaticParts, disposeObjectTree } from './meshBake';
import { damageModel } from './damageForms';
import { GROUND_DUST, type BurstDetail, type ExplosionField, type PieceSpec } from './explosionLook';

const { STEEL, GLASS, INSIDE } = DROPSHIP_COLOURS;
const METAL = '#2b2d30', CHAR = '#1c1917';

/**
 * The look's dials (people, seconds; per second where a rate). `fire`: a struck point's flame in the fall; `trail`: the smoke
 * left behind (its rate rises with the speed — a puff every `spacing` people — between `min` and `max`); `burn`: a wreck on fire
 * (its fire dying over its last `fade`), its black smoke; `smoulder`: the grey smoke after, to the wreck's decay; `sink`: how
 * far a wreck settles in its last seconds; `skid`: the impact's spray ahead along the slide; `breakup`: the metal thrown.
 */
export const SHIP_CRASH_LOOK = {
  fire: { rate: 30, life: 0.4, s0: 0.16, s1: 0.5 },
  trail: { spacing: 1.6, min: 6, max: 22, life: 3.6, s0: 0.5, s1: 2.8, color0: '#0e0c0b', color1: '#36322f' },
  burn: { seconds: 45, fade: 15, fireRate: 16, fireLife: 0.7, fire0: 0.18, fire1: 0.55, smokeRate: 2.4, smokeLife: 9, smoke0: 0.6, smoke1: 2.2, rise: 3.2, color0: '#1b1816', color1: '#4a4541' },
  smoulder: { rate: 1.4, life: 8, s0: 0.6, s1: 2.6, rise: 1.4, color0: '#5d5852', color1: '#9a958f' },
  sink: { seconds: 8, depth: 0.45 },
  skid: { dust: 22, debris: 14, reach: 0.35 },
  breakup: { debris: 16, speed: 7 },
} as const;

/** Where the hull breaks (the profiles' z, a person 1.8): behind the cab, and ahead of the rear nacelles. */
export const WRECK_CUTS = { nose: 1.6, tail: -2.3 } as const;
/** How far the airframe sits down on its belly: the profiles' y at the ground (the strongback just under it). */
const SINK = 3.32;
/** Each piece's z window in the profiles' frame. */
const WINDOW: Record<WreckPiece, [number, number]> = {
  whole: [-Infinity, Infinity], nose: [WRECK_CUTS.nose, Infinity], aft: [-Infinity, WRECK_CUTS.nose], mid: [WRECK_CUTS.tail, WRECK_CUTS.nose], tail: [-Infinity, WRECK_CUTS.tail], heli: [-Infinity, Infinity],
};
/** A fixed damage seed a piece (every wreck of a piece the same, so its hit shape is the model's). */
const SEED: Record<WreckPiece, number> = { whole: 11, nose: 12, aft: 13, mid: 14, tail: 15, heli: 16 };

/** A side profile (z, y pairs) clipped to z in [lo, hi] (Sutherland–Hodgman on the two lines); null when under a sliver. */
export function clipProfile(pts: Pts, lo: number, hi: number): Pts | null {
  let poly: Array<[number, number]> = [];
  for (let i = 0; i < pts.length; i += 2) poly.push([pts[i], pts[i + 1]]);
  for (const [edge, keep] of [[lo, (z: number) => z >= lo], [hi, (z: number) => z <= hi]] as const) {
    if (!Number.isFinite(edge)) continue;
    const out: Array<[number, number]> = [];
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i], b = poly[(i + 1) % poly.length], ina = keep(a[0]), inb = keep(b[0]);
      if (ina) out.push(a);
      if (ina !== inb) { const t = (edge - a[0]) / (b[0] - a[0]); out.push([edge, a[1] + t * (b[1] - a[1])]); }
    }
    poly = out;
  }
  if (poly.length < 3) return null;
  let zmin = Infinity, zmax = -Infinity;
  for (const [z] of poly) { zmin = Math.min(zmin, z); zmax = Math.max(zmax, z); }
  return zmax - zmin < 0.05 ? null : poly.flat();
}

/** A built wreck: its root (origin on the ground at the piece's middle, a person 1.8), where it fires from, and where it sat in the ship. */
export interface ShipWreckModel {
  root: THREE.Group;
  /** the points it burns from (the stubs, the breaks, the cab) — the first the smoke's */
  fires: THREE.Object3D[];
  /** its place in the flying ship's frame (a person 1.8): its origin's height and z there (the break-up's pieces start from it) */
  inShip: { y: number; z: number };
}

/**
 * The wreck of `piece` in the ship's palette `p` (scorched: the host passes SCORCHED): the airframe's parts within the piece's
 * window brought down to the ground (`SINK`), its nacelles torn off at the stubs, its breaks' torn edges, 63.4's scorch and
 * knocked-off small parts (seeded by the piece, so every wreck of a piece is the same). Parts are tagged `fitPart` for the hit
 * shape (body, cab, tail, the sponsons); the torn edges, the fins and the nacelle lying by it are `thin` (no target). Not baked.
 */
/**
 * 69.59: a helicopter's wreck — lane H's look C (rendering/helicopterLooks.ts `armed`, a person 1.8) down on its belly in the
 * palette `p` (scorched), its wheels gone under it (`HELI_SINK`), rolled a little onto its side, its rotors torn off (main and
 * tail); it burns at its engines and its cabin. Its hit shape is two boxes, the cabin ahead of the boom's root and the boom with
 * its fin behind (rendering/hitShapeFit.ts fitHeliWreck; constants/hitboxes.ts SHIP_WRECK_PIECES.heli). Not baked.
 */
export const HELI_WRECK = { sink: 0.5, roll: 0.14, boomZ: -3.3 } as const;
export function buildHeliWreck(p: UnitPalette): ShipWreckModel {
  const m = buildHelicopter('armed', p, 1.8), root = new THREE.Group();
  for (const sp of m.spin) sp.group.parent?.remove(sp.group);   // (the rotors gone)
  m.root.position.y = -HELI_WRECK.sink;
  m.body.rotation.z = HELI_WRECK.roll;
  root.add(m.root);
  const fires = [...m.exhausts, group(m.body, 0, 1.8, 0.6)];
  return { root, fires, inShip: { y: 0, z: 0 } };
}

export function buildShipWreck(p: UnitPalette, piece: WreckPiece): ShipWreckModel {
  const root = new THREE.Group(), hull = group(root, 0, -SINK, 0), D = DROPSHIP, N = D.nacelle;
  const [lo, hi] = WINDOW[piece];
  const tag = <T extends THREE.Object3D>(o: T, part: string | null): T => { if (part) o.userData.fitPart = part; else o.userData.thin = true; return o; };
  const inside = (z: number) => z >= lo && z < hi;
  const clipSlab = (pts: Pts, w: number, color: string, x: number, c: number, part: string | null) => {
    const q = clipProfile(pts, lo, hi);
    return q ? tag(slab(hull, q, w, color, x, c), part) : null;
  };
  // the body, the strongback and its rails, the team's band (burnt), the sponsons
  clipSlab(BODY, 2 * D.half, p.body, 0, 0.12, 'body');
  clipSlab(rect(-3.8, 3.27, 3.4, 3.37), 2 * D.half - 0.2, p.dark, 0, 0.03, 'body');
  for (const side of [-1, 1]) {
    clipSlab(rect(-3.5, 3.17, 3.1, 3.31), 0.18, STEEL, side * (D.clampX - 0.12), 0, 'body');
    clipSlab(rect(-3.3, 4.12, 2.6, 4.3), 0.02, p.flash, side * (D.half + 0.01), 0, 'body');
    clipSlab(SPONSON, 2 * D.sponsonHalf, p.body, side * D.sponsonX, 0.08, side > 0 ? 'sponsonL' : 'sponsonR');
    for (const z of [0.3, -1.3]) if (inside(z)) tag(top(hull, rect(side * (D.sponsonX - 0.24), z - 0.45, side * (D.sponsonX + 0.24), z + 0.45), 0.03, INSIDE, 4.5), side > 0 ? 'sponsonL' : 'sponsonR');
  }
  // the cab and its glass (the nose's)
  if (inside(3)) {
    tag(slab(hull, CAB, 2.6, p.body, 0, 0.1), 'cab');
    for (const [a, b, w] of [[[4.8, 4.35], [4.1, 5.15], 2.3], [[4.0, 3.55], [4.7, 3.95], 1.8]] as const) {
      const dz = b[0] - a[0], dy = b[1] - a[1], len = Math.hypot(dz, dy), uz = dz / len, uy = dy / len, nz = uy, ny = -uz, t = 0.025, i = 0.1;
      const p0 = [a[0] + uz * i, a[1] + uy * i], p1 = [b[0] - uz * i, b[1] - uy * i];
      tag(slab(hull, [p0[0], p0[1], p1[0], p1[1], p1[0] + nz * t, p1[1] + ny * t, p0[0] + nz * t, p0[1] + ny * t], w, GLASS, 0), 'cab');
    }
    for (const side of [-1, 1]) {
      tag(slab(hull, [3.3, 4.68, 4.45, 4.68, 4.05, 5.08, 3.3, 5.1], 0.06, GLASS, side * 1.3), 'cab');
      tag(slab(hull, rect(2.0, 4.66, 2.9, 5.15), 0.03, p.body, side * 1.315, 0.012), 'cab');   // the crew door (its window blown out)
    }
  }
  // the tail, its nozzles' stumps and its fins (the fins no target, as the flying ship's shape has them)
  if (inside(-4)) {
    tag(slab(hull, TAIL, 2.2, p.body, 0, 0.1), 'tail');
    for (const side of [-1, 1]) {
      tag(box(hull, 0.5, 0.5, 0.3, STEEL, side * 0.62, 4.02, -4.75), 'tail');
      const f = group(hull, side * 0.8, 4.5, 0); f.rotation.z = -side * 0.42;
      tag(slab(f, [-3.6, 0, -4.6, 0, -4.8713, 0.62, -4.2588, 0.62], 0.12, p.body, 0, 0.03), null);
    }
  }
  // the nacelles' stubs, torn off at the shaft (their collars left), within the window
  const fires: THREE.Object3D[] = [];
  for (const side of [-1, 1]) for (const z of N.z) {
    if (!inside(z)) continue;
    const reach = 2.62 - (D.sponsonX + D.sponsonHalf);
    tag(slab(hull, [z - 0.35, 3.82, z + 0.35, 3.82, z + 0.25, 4.3, z - 0.25, 4.3], reach, p.body, side * (D.sponsonX + D.sponsonHalf + reach / 2), 0.04), side > 0 ? 'sponsonL' : 'sponsonR');
    tag(box(hull, 0.06, 0.4, 0.5, CHAR, side * 2.63, 4.06, z), side > 0 ? 'sponsonL' : 'sponsonR');   // the torn face, blackened
    fires.push(anchor(hull, side * 2.55, 4.3, z));
  }
  // the breaks: a blackened bulkhead just proud of each cut face, the skin's torn edges standing out past it
  for (const cut of [lo, hi]) {
    if (!Number.isFinite(cut)) continue;
    const out = cut === hi ? 1 : -1;
    tag(slab(hull, rect(Math.min(cut - out * 0.06, cut + out * 0.03), 3.45, Math.max(cut - out * 0.06, cut + out * 0.03), 4.5), 2 * D.half - 0.3, INSIDE, 0), 'body');
    for (let i = 0; i < 6; i++) {
      const u = (i + 0.5) / 6, side = i % 2 ? 1 : -1, onTop = i < 3;
      const s = box(hull, onTop ? 0.7 : 0.04, onTop ? 0.04 : 0.55, 0.32 + 0.12 * (i % 3), i % 3 ? p.body : CHAR,
        onTop ? (u * 2 - 1) * 1.2 : side * (D.half - 0.02), onTop ? 4.6 : 3.7 + 0.25 * (i % 2), cut + out * 0.14);
      s.rotation.set(out * (onTop ? 0.5 + 0.3 * (i % 2) : 0.2), 0.3 * side, onTop ? 0.25 * side : 0);
      tag(s, null);
    }
    fires.unshift(anchor(hull, 0, 4.4, cut));
  }
  if (inside(3) && fires.length === 0) fires.push(anchor(hull, 0, 5.0, 3.2));
  if (piece === 'whole') fires.unshift(anchor(hull, 0, 4.7, -0.8));
  // a nacelle torn off, lying by the hull (no target: a part thrown, walked over as debris is)
  if (piece === 'whole' || piece === 'aft' || piece === 'tail') {
    const n = group(root, -3.5, 0.42, piece === 'whole' ? -1.6 : -0.4);
    n.rotation.set(0.12, 0.5, 1.2);
    nacelleBody(n, p);
    n.traverse(o => { o.userData.thin = true; });
  }
  // (the profiles stay put: their origin is the hull's, so 63.4's knock askew would swing one far off — boxes and rods only)
  const extruded: THREE.Object3D[] = [...fires];
  root.traverse(o => { if ((o as THREE.Mesh).isMesh && (o as THREE.Mesh).geometry.type === 'ExtrudeGeometry') extruded.push(o); });
  damageModel(root, SEED[piece], 1, extruded);
  root.traverse(o => { if ((o as THREE.Mesh).isMesh && !o.userData.fitPart) o.userData.thin = true; });   // (63.4's scorch patches: decals, no target)
  // its middle: the piece's own box centred on its origin (the structure stands at the piece's middle)
  root.updateMatrixWorld(true);
  const bounds = new THREE.Box3();
  root.traverse(o => { if ((o as THREE.Mesh).isMesh && o.userData.fitPart) bounds.union(new THREE.Box3().setFromObject(o, true)); });
  const mid = (bounds.min.z + bounds.max.z) / 2;
  hull.position.z -= mid;
  for (const c of root.children) if (c !== hull) c.position.z -= mid;
  return { root, fires, inShip: { y: SINK + D.lift, z: mid } };
}

// ---------------------------------------------------------------------------
// The fall
// ---------------------------------------------------------------------------

/** A crash's frame in the world (core/shipCrash.ts crashPose's): its origin, up, forward and left (the ship's +x). */
export interface CrashFrame { origin: Vec3; up: Vec3; front: Vec3; left: Vec3 }

function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => { a = (a + 0x6d2b79f5) >>> 0; let t = a; t = Math.imul(t ^ (t >>> 15), t | 1); t ^= t + Math.imul(t ^ (t >>> 7), t | 61); return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
}

const _bx = new THREE.Vector3(), _by = new THREE.Vector3(), _bz = new THREE.Vector3(), _bm = new THREE.Matrix4(), _q = new THREE.Quaternion(), _qt = new THREE.Quaternion();
const _w = new THREE.Vector3(), _v = new THREE.Vector3(), _u = new THREE.Vector3(), _d = new THREE.Vector3(), _X = new THREE.Vector3(1, 0, 0);
/** One reused spec for every emit (the field reads it at once). */
const spec: PieceSpec & { at: THREE.Vector3; vel: THREE.Vector3; up: THREE.Vector3; ground: THREE.Vector3 } = { at: new THREE.Vector3(), vel: new THREE.Vector3(), up: new THREE.Vector3(), ground: new THREE.Vector3(), life: 1, s0: 1, s1: 1, c0: '#000' };
function reset(s: typeof spec): typeof spec { s.drag = 0; s.gravity = 0; s.delay = 0; s.spin = 0; s.flat = false; s.c1 = undefined; s.vel.set(0, 0, 0); return s; }

/** A flame and a puff of smoke at `at` (world), the smoke left with `trailVel`: a burning point's one beat. */
function flame(field: ExplosionField, at: THREE.Vector3, up: THREE.Vector3, vel: THREE.Vector3, P: number, rnd: () => number, far: boolean): void {
  if (far) return;
  const F = SHIP_CRASH_LOOK.fire, s = reset(spec);
  s.at.copy(at); s.up.copy(up); s.ground.copy(at);
  s.vel.copy(vel).multiplyScalar(0.8).addScaledVector(up, 1.2 * P).add(_d.set(rnd() - 0.5, rnd() - 0.5, rnd() - 0.5).multiplyScalar(1.5 * P));
  s.drag = 2.5; s.life = F.life * (0.7 + 0.6 * rnd()); s.s0 = F.s0 * P; s.s1 = F.s1 * P * (0.8 + 0.4 * rnd()); s.c0 = rnd() < 0.5 ? '#ffb347' : '#ffd27a';
  field.emit('fireball', s);
}

/**
 * A lost flight's fall drawn: the flight's model (kept from its flight, `ship`) placed on the crash's frame each frame, struck
 * by the cause — the nacelles `lost` (the sim's order; core/shipBody.ts SHIP_PARTS) hidden, fire where they were — and its trail;
 * after a break-up its pieces in its place (`pieces`, added to the scene by the host). `sense` the crash's spin sense (which
 * side, when the lost nacelles are not known: a view made after the loss).
 */
export class FallingShip {
  /** the break-up's pieces (empty till then) — the host adds it to the scene */
  readonly pieces = new THREE.Group();
  private readonly fires: THREE.Object3D[] = [];
  private split: Array<{ m: ShipWreckModel; root: THREE.Group; to: number; turn: number }> | null = null;
  private readonly rnd: () => number;
  private readonly last = new THREE.Vector3();
  private placed = false;
  private struck = false;
  /** the fall's velocity (world units a second), from the frames drawn */
  readonly vel = new THREE.Vector3();
  private fireAcc = 0;
  private smokeAcc = 0;

  constructor(readonly ship: DropshipModel, readonly cause: ShipCrash['cause'], sense: number, lost: readonly boolean[], private readonly palette: UnitPalette, private readonly seed: number) {
    this.rnd = mulberry32(seed * 7919 + 3);
    const N = DROPSHIP.nacelle;
    let gone = [0, 1, 2, 3].filter(k => lost[k]);
    if (cause === 'nacelle' && gone.length < 2) gone = sense > 0 ? [0, 2] : [1, 3];   // (the side it rolls to)
    for (const k of gone) {
      ship.nacelles[k].visible = false;
      const side = k % 2 === 0 ? 1 : -1, z = k < 2 ? N.z[0] : N.z[1];
      this.fires.push(anchor(ship.hull, side * (N.x - 0.55), N.y, z));
    }
    if (cause === 'cockpit') this.fires.unshift(anchor(ship.hull, 0, 5.0, 3.0));
    if (cause === 'hull') this.fires.unshift(anchor(ship.hull, 0, 4.7, -0.6), anchor(ship.hull, sense * DROPSHIP.sponsonX, 4.4, -1.3));
    if (!this.fires.length) this.fires.push(anchor(ship.hull, 0, 4.7, -0.6));
  }

  /** The struck points (world), the first the trail's. */
  get fireCount(): number { return this.split ? this.split.reduce((n, s) => n + s.m.fires.length, 0) : this.fires.length; }

  /**
   * Place the fall at `f` and draw its fire and trail `dt` seconds on. `ground` the point on the ground under it (the debris'
   * floor); `broke` the share of the way from the break-up to the impact (null: whole) and the pieces it comes down in;
   * `person` world units a person; `detail` its distance's (67.54 burstDetail).
   */
  update(field: ExplosionField | null, f: CrashFrame, ground: Vec3, broke: { share: number; pieces: 1 | 2 | 3 } | null, dt: number, person: number, detail: BurstDetail): void {
    const { ship } = this;
    _bx.set(f.left.x, f.left.y, f.left.z); _by.set(f.up.x, f.up.y, f.up.z); _bz.set(f.front.x, f.front.y, f.front.z);
    _q.setFromRotationMatrix(_bm.makeBasis(_bx, _by, _bz));
    ship.root.position.set(f.origin.x, f.origin.y, f.origin.z);
    ship.root.quaternion.copy(_q);
    if (this.placed && dt > 0) this.vel.set(f.origin.x - this.last.x, f.origin.y - this.last.y, f.origin.z - this.last.z).divideScalar(dt);
    this.last.set(f.origin.x, f.origin.y, f.origin.z);
    this.placed = true;
    _u.set(ground.x - f.origin.x, ground.y - f.origin.y, ground.z - f.origin.z).normalize().negate();   // the ground's up under it
    if (broke && !this.split) this.breakUp(field, f, ground, broke.pieces, person);
    if (this.split) {
      const k = person / 1.8, share = Math.min(1, Math.max(0, broke?.share ?? 1));
      _d.copy(this.vel).addScaledVector(_u, -this.vel.dot(_u));
      if (_d.lengthSq() < 1e-9) _d.copy(_bz);
      _d.normalize();
      for (const s of this.split) {
        // each piece from its place in the ship toward its place on the line (core/shipCrash.ts wreckPieces), turning off the fall
        _v.set(0, s.m.inShip.y, s.m.inShip.z).multiplyScalar(k).applyQuaternion(_q);
        s.root.position.set(f.origin.x, f.origin.y, f.origin.z).addScaledVector(_v, 1 - share).addScaledVector(_d, s.to * person * share);
        s.root.quaternion.copy(_q).multiply(_qt.setFromAxisAngle(_X, s.turn * share));
        s.root.updateMatrixWorld(true);
      }
    } else ship.root.updateMatrixWorld(true);
    if (!field) return;
    const P = person, far = detail === 'far', rnd = this.rnd;
    const points = this.split ? this.split.flatMap(s => s.m.fires.slice(0, 2)) : this.fires;
    if (!this.struck) for (const a of points) field.spawn('law', a.getWorldPosition(_w), _u, 'rock', 'far', this.seed + 1);   // the loss: a burst at each struck point
    this.struck = true;
    // the flames: each struck point, every beat
    this.fireAcc += SHIP_CRASH_LOOK.fire.rate * dt * (far ? 0.4 : 1);
    for (; this.fireAcc >= 1; this.fireAcc--) for (const a of points) flame(field, a.getWorldPosition(_w), _u, this.vel, P, rnd, false);
    // the trail: from the first point, a puff every `spacing` people of the fall
    const T = SHIP_CRASH_LOOK.trail, speed = this.vel.length() / P;
    this.smokeAcc += Math.min(T.max, Math.max(T.min, speed / T.spacing)) * dt * (far ? 0.6 : 1);
    for (; this.smokeAcc >= 1; this.smokeAcc--) {
      const lead = this.split ? this.split.map(s => s.m.fires[0]).filter(Boolean) : [points[0]];
      for (const a of lead) {
        const s = reset(spec);
        s.at.copy(a.getWorldPosition(_w)).addScaledVector(this.vel, -dt * rnd());   // (spread along the frame's step: no beads)
        s.up.copy(_u); s.ground.copy(s.at);
        s.vel.copy(this.vel).multiplyScalar(0.12).addScaledVector(_u, 0.5 * P).add(_d.set(rnd() - 0.5, rnd() - 0.5, rnd() - 0.5).multiplyScalar(0.8 * P));
        s.drag = 0.6; s.life = T.life * (0.75 + 0.5 * rnd()); s.s0 = T.s0 * P; s.s1 = T.s1 * P * (0.8 + 0.4 * rnd()) * (far ? 1.35 : 1);
        s.c0 = T.color0; s.c1 = T.color1; s.spin = 0.3 * rnd();
        field.emit('smoke', s);
      }
    }
  }

  /** The break-up: a burst, metal thrown, and the ship drawn as its pieces from now on. */
  private breakUp(field: ExplosionField | null, f: CrashFrame, ground: Vec3, pieces: 1 | 2 | 3, person: number): void {
    this.ship.root.visible = false;
    const spread = SHIP_CRASH.spreadPeople * SHIP_CRASH.brokeUpSpread;
    this.split = wreckPieces(pieces).map((o, i) => {
      const m = buildShipWreck(this.palette, o.piece);
      bakeStaticParts(m.root);
      const root = new THREE.Group(), inner = m.root;
      inner.scale.setScalar(person / 1.8);
      root.add(inner);
      this.pieces.add(root);
      return { m, root, to: o.z * spread, turn: (i % 2 ? -1 : 1) * (0.5 + 0.6 * this.rnd()) };
    });
    if (!field) return;
    const at = _w.set(f.origin.x, f.origin.y, f.origin.z).addScaledVector(_by, 1.4 * person);
    field.spawn('bomb', at, _u, 'rock', 'far', this.seed + 2);
    const B = SHIP_CRASH_LOOK.breakup, rnd = this.rnd;
    for (let i = 0; i < B.debris; i++) {
      const s = reset(spec);
      s.at.copy(at); s.up.copy(_u); s.ground.set(ground.x, ground.y, ground.z);
      s.vel.copy(this.vel).add(_d.set(rnd() - 0.5, rnd() - 0.2, rnd() - 0.5).normalize().multiplyScalar(B.speed * person * (0.5 + rnd())));
      s.gravity = 9 * person; s.drag = 0.1; s.life = 9; s.s0 = s.s1 = (0.07 + 0.16 * rnd()) * person; s.c0 = rnd() < 0.5 ? METAL : this.palette.body; s.spin = 6;
      field.emit('debris', s);
    }
  }

  /** The pieces' own meshes (the ship's model is the host's). */
  dispose(): void { disposeObjectTree(this.pieces); }
}

// ---------------------------------------------------------------------------
// The impact
// ---------------------------------------------------------------------------

/**
 * The crash come down: at each piece's ground point (`at`, the first the ship's own) a heavy shell's burst (67.54), the first a
 * fuel fire besides (a vehicle's death); and, near enough to see it, a spray of the ground's dust and the ship's metal thrown
 * ahead along `along` (the slide's direction on the ground) at up to `speed` (world units a second). `up` the ground's up.
 */
export function crashImpact(field: ExplosionField, at: readonly Vec3[], up: Vec3, along: Vec3, speed: number, person: number, material: GroundMaterial, detail: BurstDetail, seed: number): void {
  at.forEach((p, i) => field.spawn('heavyShell', p, up, material, detail, seed + i));
  if (at.length) field.spawn('vehicleDeath', at[0], up, material, detail, seed + 7);
  if (detail === 'far') return;
  const S = SHIP_CRASH_LOOK.skid, rnd = mulberry32(seed * 31 + 5), P = person;
  _u.set(up.x, up.y, up.z).normalize();
  _bz.set(along.x, along.y, along.z);
  _bz.addScaledVector(_u, -_u.dot(_bz)).normalize();
  _bx.crossVectors(_u, _bz).normalize();
  const fling = Math.max(4 * P, Math.min(speed * S.reach, 14 * P));
  for (const p of at) {
    for (let i = 0; i < (detail === 'mid' ? S.dust / 2 : S.dust); i++) {
      const s = reset(spec), a = (rnd() - 0.5) * 1.2;
      s.at.set(p.x, p.y, p.z).addScaledVector(_u, 0.2 * P); s.up.copy(_u); s.ground.copy(s.at);
      s.vel.copy(_bz).multiplyScalar(Math.cos(a)).addScaledVector(_bx, Math.sin(a)).multiplyScalar(fling * (0.4 + 0.6 * rnd())).addScaledVector(_u, 1.2 * P * rnd());
      s.drag = 1.6; s.life = 2.2 + 1.4 * rnd(); s.s0 = 0.5 * P; s.s1 = (1.4 + 1.2 * rnd()) * P; s.flat = true;
      s.c0 = GROUND_DUST[material] ?? GROUND_DUST.soil; s.c1 = '#cfc9bf';
      field.emit('dust', s);
    }
    if (detail !== 'full') continue;
    for (let i = 0; i < S.debris; i++) {
      const s = reset(spec), a = (rnd() - 0.5) * 1.6;
      s.at.set(p.x, p.y, p.z).addScaledVector(_u, 0.4 * P); s.up.copy(_u); s.ground.set(p.x, p.y, p.z);
      s.vel.copy(_bz).multiplyScalar(Math.cos(a)).addScaledVector(_bx, Math.sin(a)).multiplyScalar(fling * (0.5 + 0.7 * rnd())).addScaledVector(_u, (4 + 4 * rnd()) * P);
      s.gravity = 9 * P; s.drag = 0.2; s.life = 3 + 2 * rnd(); s.s0 = s.s1 = (0.12 + 0.22 * rnd()) * P; s.c0 = METAL; s.spin = 7;
      field.emit('debris', s);
    }
  }
}

// ---------------------------------------------------------------------------
// The wreck
// ---------------------------------------------------------------------------

/** A wreck's look over its life `age` (seconds since it came down): 1 burning … 0 out (its fire), its smoke's share, its sink (people). */
export function wreckPhase(age: number, decay = SHIP_CRASH.decayTicks * PHYS_DT): { fire: number; smoke: number; black: boolean; sink: number } {
  const B = SHIP_CRASH_LOOK.burn, K = SHIP_CRASH_LOOK.sink;
  const fire = age < B.seconds - B.fade ? 1 : Math.max(0, (B.seconds - age) / B.fade);
  const smoke = age < B.seconds ? 1 : Math.max(0.25, 1 - (age - B.seconds) / Math.max(1, decay - B.seconds));
  const sink = K.depth * Math.min(1, Math.max(0, (age - (decay - K.seconds)) / K.seconds));
  return { fire, smoke, black: age < B.seconds, sink };
}

/**
 * A wreck standing (a `shipWreck` structure): its model baked (one mesh a material), placed by the host on the drawn ground at
 * its frame; `update` burns it, smokes it and settles it by its age. `person` world units a person; `seed` its own (its puffs').
 */
export class WreckView {
  readonly root = new THREE.Group();
  private readonly model: ShipWreckModel;
  private readonly rnd: () => number;
  private fireAcc = 0;
  private smokeAcc = 0;
  /** draws and triangles of its baked model (for the cost) */
  readonly cost: { draws: number; triangles: number };

  constructor(readonly piece: WreckPiece, palette: UnitPalette, private readonly person: number, seed: number) {
    this.model = piece === 'heli' ? buildHeliWreck(palette) : buildShipWreck(palette, piece);   // (69.59: a helicopter's own)
    bakeStaticParts(this.model.root);
    this.model.root.scale.setScalar(person / 1.8);
    this.root.add(this.model.root);
    this.rnd = mulberry32(seed * 104729 + 17);
    let draws = 0, triangles = 0;
    this.model.root.traverse(o => { const m = o as THREE.Mesh; if (!m.isMesh) return; draws++; triangles += (m.geometry.index ? m.geometry.index.count : m.geometry.attributes.position.count) / 3; });
    this.cost = { draws, triangles };
  }

  /** Settle, burn and smoke it at `age` seconds (`dt` since the last frame); the host has placed `root` (its world matrix current). */
  update(field: ExplosionField | null, age: number, dt: number, detail: BurstDetail): void {
    const ph = wreckPhase(age), P = this.person, rnd = this.rnd;
    this.model.root.position.y = -ph.sink * 1.8;
    this.model.root.updateMatrixWorld(true);
    if (!field || dt <= 0) return;
    _u.set(0, 1, 0).transformDirection(this.root.matrixWorld);
    const far = detail === 'far', fires = this.model.fires;
    // the flames: its burning points (two from far off)
    const B = SHIP_CRASH_LOOK.burn;
    if (ph.fire > 0) {
      this.fireAcc += B.fireRate * ph.fire * dt * (far ? 0.4 : 1);
      for (; this.fireAcc >= 1; this.fireAcc--) {
        const a = fires[Math.floor(rnd() * Math.min(fires.length, far ? 2 : fires.length))];
        if (!a) break;
        const s = reset(spec);
        s.at.copy(a.getWorldPosition(_w)).add(_d.set(rnd() - 0.5, 0, rnd() - 0.5).multiplyScalar(0.6 * P)); s.up.copy(_u); s.ground.copy(s.at);
        s.vel.copy(_u).multiplyScalar((1.2 + 1.2 * rnd()) * P);
        s.drag = 1.2; s.life = B.fireLife * (0.7 + 0.6 * rnd()); s.s0 = B.fire0 * P; s.s1 = B.fire1 * P * (0.8 + 0.5 * rnd()); s.c0 = rnd() < 0.6 ? '#ffb347' : '#ffd27a';
        field.emit('fireball', s);
      }
    }
    // the smoke: black while it burns, grey after, thinning to its end — from its first point
    const S = ph.black ? { rate: B.smokeRate, life: B.smokeLife, s0: B.smoke0, s1: B.smoke1, rise: B.rise, color0: B.color0, color1: B.color1 } : SHIP_CRASH_LOOK.smoulder;
    this.smokeAcc += S.rate * ph.smoke * dt;
    const a = fires[0];
    for (; a && this.smokeAcc >= 1; this.smokeAcc--) {
      const s = reset(spec);
      s.at.copy(a.getWorldPosition(_w)).add(_d.set(rnd() - 0.5, 0, rnd() - 0.5).multiplyScalar(0.8 * P)); s.up.copy(_u); s.ground.copy(s.at);
      s.vel.copy(_u).multiplyScalar(S.rise * P * (0.8 + 0.4 * rnd())).add(_d.set(rnd() - 0.5, 0, rnd() - 0.5).multiplyScalar(0.3 * P));
      s.drag = 0.2; s.life = S.life * (0.75 + 0.5 * rnd()); s.s0 = S.s0 * P; s.s1 = S.s1 * P * (0.8 + 0.4 * rnd()) * (far ? 1.35 : 1);
      s.c0 = S.color0; s.c1 = S.color1; s.spin = 0.25 * rnd();
      field.emit('smoke', s);
    }
  }

  dispose(): void { disposeObjectTree(this.root); }
}

/** A falling ship's nacelles lost, from the flight's parts as last drawn (core/shipBody.ts SHIP_PARTS: the four nacelles first, ≤ 0 lost). */
export function lostNacelles(parts: readonly number[] | undefined): boolean[] {
  return [0, 1, 2, 3].map(k => parts !== undefined && (parts[k] ?? 1) <= 0);
}
